import {ADJACENT_GAP, EPSILON} from './constants.js'
import {overlay_height} from './frame.js'

// Items on a track that overlap in time are stacked into extra lanes
export function pack_lanes (items, key, len_of) {
	const lane_ends = [];
	const packed = _.map(_.sortBy(items, "start"), (item) => {
		let lane = _.findIndex(lane_ends, (end) => end <= item.start);
		if (lane < 0) lane = lane_ends.length;
		lane_ends[lane] = item.start + (len_of ? len_of(item) : item.duration);
		return {[key]: item, lane};
	});
	return {items: packed, lanes: Math.max(lane_ends.length, 1)};
}

// Playback positions of the video clips: clips are stored back to back, a fade between touching clips overlaps them
export function compute_layout (timeline) {
	const entries = _.map(timeline, (clip, gindex) => {
		const len = clip.out - clip.in;
		return {clip, gindex, track: clip.track, len, start: clip.start, end: clip.start + len};
	});
	_.each(_.groupBy(entries, "track"), (list) => {
		const sorted = _.sortBy(list, "start");
		// A fade between touching clips overlaps them by the fade length (like iMovie),
		// so both play in full during the cross-fade. The rest of that chain of touching clips moves earlier with it.
		let shift = 0;
		_.each(sorted, (entry, order) => {
			// Within a track, clips that start later are drawn on top
			entry.order = order;
			const prev = sorted[order - 1];
			const touching = prev && Math.abs(prev.clip.start + prev.len - entry.clip.start) <= ADJACENT_GAP;
			if (!touching) shift = 0;
			const tr = entry.clip.transition;
			if (touching && tr.type == "fade" && tr.duration > 0) {
				const length = Math.min(tr.duration, entry.len, prev.len);
				entry.xfade = {length, prev_id: prev.clip.id};
				shift += length;
			}
			entry.start = entry.clip.start - shift;
			entry.end = entry.start + entry.len;
		});
		// The out transition is skipped when the next clip already cross-fades over this one
		const crossfaded = _.keyBy(_.filter(sorted, "xfade"), "xfade.prev_id");
		_.each(sorted, (entry) => {
			const out = entry.clip.transition_out;
			entry.fade_out = out && out.type == "fade" && out.duration > 0 && !crossfaded[entry.clip.id] ? Math.min(out.duration, entry.len) : 0;
		});
	});
	return entries;
}

export function build_track_rows (tracks, layout, overlays, audios) {
	return _.map(tracks, (track, row) => {
		const base = {id: track.id, kind: track.kind, name: track.name, hidden: track.hidden, muted: !!track.muted, row};
		if (track.kind == "video") {
			// Video clips never overlap apart from cross-fades, so they share a single lane
			const items = _.map(_.sortBy(_.filter(layout, (l) => l.track == track.id), "start"), (l) => Object.assign({}, l, {lane: 0}));
			return Object.assign(base, {items, lanes: 1});
		}
		if (track.kind == "overlay") return Object.assign(base, pack_lanes(_.filter(overlays, (o) => o.track == track.id), "overlay"));
		return Object.assign(base, pack_lanes(_.filter(audios, (a) => a.track == track.id), "audio"));
	});
}

// In stored positions, where cross-faded clips still sit back to back
export function track_end (timeline, track_id) {
	return _.max(_.map(_.filter(timeline, (c) => c.track == track_id), (c) => c.start + (c.out - c.in))) || 0;
}

// Video clips on one track never overlap, so a clip can only be placed in a free gap
export function other_clips (timeline, clip, track_id) {
	return _.sortBy(_.filter(timeline, (c) => c.track == track_id && c.id != clip.id), "start");
}

export function fit_start (timeline, clip, track_id, want) {
	const len = clip.out - clip.in;
	let best;
	let gap_start = 0;
	// Walk the free gaps between the other clips and take the position nearest to the wanted one
	_.each(other_clips(timeline, clip, track_id).concat([{start: Infinity, in: 0, out: 0}]), (c) => {
		if (c.start - gap_start >= len - EPSILON) {
			const pos = _.clamp(want, gap_start, Math.max(gap_start, c.start - len));
			if (best === undefined || Math.abs(pos - want) < Math.abs(best - want)) best = pos;
		}
		gap_start = Math.max(gap_start, c.start + (c.out - c.in));
	});
	return best;
}

// Room left around a clip: end of the previous clip and start of the next one
export function clip_bounds (timeline, clip) {
	const others = other_clips(timeline, clip, clip.track);
	const before = _.filter(others, (c) => c.start < clip.start);
	const after = _.filter(others, (c) => c.start >= clip.start);
	return {
		prev_end: _.max(_.map(before, (c) => c.start + (c.out - c.in))) || 0,
		next_start: after.length ? after[0].start : Infinity
	};
}

export function max_len (timeline, clip) {
	return clip_bounds(timeline, clip).next_start - clip.start;
}

// Pushes overlapping clips on the same track later; mutates the clips
export function resolve_overlaps (timeline) {
	_.each(_.groupBy(timeline, "track"), (list) => {
		let end = 0;
		_.each(_.sortBy(list, "start"), (c) => {
			c.start = Math.max(c.start, end);
			end = c.start + (c.out - c.in);
		});
	});
}

// The frontmost visible overlay or clip at a point of the preview (in % of the frame)
export function pick_item ({overlays, layout, hidden_ids, track_z_map}, px, py, t) {
	const inside = (o) => px >= o.x && px <= o.x + o.w && py >= o.y && py <= o.y + overlay_height(o);
	let best;
	let best_z = -1;
	_.each(overlays, (o) => {
		if (hidden_ids[o.track] || t < o.start || t >= o.start + o.duration || !inside(o)) return;
		const z = track_z_map[o.track] + 500;
		if (z > best_z) {
			best = {overlay: o};
			best_z = z;
		}
	});
	_.each(layout, (l) => {
		if (hidden_ids[l.track] || t < l.start || t >= l.end || !inside(l.clip)) return;
		const z = track_z_map[l.track] + l.order + 1;
		if (z > best_z) {
			best = {clip: l.clip};
			best_z = z;
		}
	});
	return best;
}
